export interface BlockedEnvironmentsResultsFields {
  partner: {
    name: string;
    uuid: string;
  };
  company: {
    name: string;
    uuid: string;
  };
  environment: {
    name: string;
    uuid: string;
    provider: string;
    region: string;
    model_type: string;
  };
  blocked_at: '';
  elapsed_blocking_time: number;
}

export interface EnvironmentHealthResultsFields {
  partner: {
    name: string;
    uuid: string;
  };
  company: {
    name: string;
    uuid: string;
  };
  environment: {
    name: string;
    uuid: string;
    provider: string;
    publication_type: string;
    region: string;
    model_type: string;
  };
  instances_running: number;
  max_connections: number;
  connections: number;
  vacancies: number;
  total_licenses: number;
  period_margin: number;
  license_margin: number;
}

export interface InstanceStatusResultsFields {
  partner: {
    name: string;
    uuid: string;
  };
  company: {
    name: string;
    uuid: string;
  };
  environment: {
    name: string;
    uuid: string;
    provider: string;
  };
  flavor: string;
  zone: string;
  status_quantity: number;
  status: string;
}

export interface InstanceWithSessionTimeoutResultsFields {
  partner: {
    name: string;
    uuid: string;
  };
  environment: {
    name: string;
    uuid: string;
    provider: string;
  };
  flavor: string;
  instance_id: string;
}

export type BlockedEnvironmentsFields = PaginatedResponse<
  BlockedEnvironmentsResultsFields
>;

export type EnvironmentHealthFields = PaginatedResponse<
  EnvironmentHealthResultsFields
>;

export type InstanceStatusFields = PaginatedResponse<
  InstanceStatusResultsFields
>;

export type InstancesWithSessionTimeoutFields = PaginatedResponse<
  InstanceWithSessionTimeoutResultsFields
>;

export interface EnvironmentAndCeleryQueueStatus {
  blocked_queues: string[];
  total_environments: number;
  total_instances: number;
  total_sessions: number;
}

export interface EnvironmentWithFailedZeroTrustFields {
  environment: { name: string; uuid: string };
  partner: { name: string; uuid: string; flag_new_front: string };
  total_sessions: number;
  total_sessions_with_failed_zero_trust: number;
  sessions_with_failed_zero_trust_percentage: number;
}
export interface EnvironmentsWithFailedZeroTrustResponse
  extends PaginatedResponse<EnvironmentWithFailedZeroTrustFields> {
  total_count: number;
}

export interface SessionsHistoryMonitorGraph {
  timestamp: number;
  licenses: number;
  sessions: {
    max: number;
    avg: number;
  };
  vacancies: {
    max: number;
    avg: number;
  };
  instances: {
    max: number;
    avg: number;
  };
}

export interface ClientsHistoryMonitorGraph {
  total_created_clients: number;
  created_clients_in_period: {
    count: number;
    period_in_days: number;
    growth_in_period_percent: number;
  };
}

export interface UsersHistoryMonitorGraph {
  total_created_users: number;
  created_users_in_period: {
    count: number;
    period_in_days: number;
    growth_in_period_percent: number;
  };
}

export interface SessionsHistoryMonitorGraphFields {
  metrics: SessionsHistoryMonitorGraph[];
}

export interface EnvironmentsCostGraph {
  timestamp: number;
  cost: string;
}

export interface EnvironmentsCostGraphFields {
  total: string;
  metrics: EnvironmentsCostGraph[];
}

export interface MonthlyEnvironmentsCostMetric {
  month: string;
  cost: string;
  total_licenses: number;
  total_instances: number;
  total_environments: number;
}

export interface MonthlyEnvironmentsCostGraphFields {
  metrics: MonthlyEnvironmentsCostMetric[];
}

export interface FlavorsCostFields {
  flavor: string;
  total_cost: string;
  num_instances: number;
  cost_percentage: string;
}

export type ListFlavorsCostFields = PaginatedResponse<FlavorsCostFields>;

export enum PeriodCountAccess {
  LAST_24_HOURS = '1d',
  LAST_48_HOURS = '2d',
  LAST_7_DAYS = '7d',
  LAST_15_DAYS = '15d',
  LAST_30_DAYS = '30d',
  LAST_60_DAYS = '60d',
  LAST_90_DAYS = '90d',
  LAST_180_DAYS = '180d',
}

export interface FlavorEnvironmentsFields {
  environment_name: string;
  environment_uuid: string;
  company_name: string;
  company_uuid: string;
  partner_name: string;
  partner_uuid: string;
  total_cost: string;
  num_instances: number;
}

export type FlavorEnvironmentsData = PaginatedResponse<
  FlavorEnvironmentsFields
>;

export interface MonitorState {
  readonly isLoading: boolean;
  readonly blockedEnvironmentsData: BlockedEnvironmentsFields;
  readonly environmentHealthData: EnvironmentHealthFields;
  readonly instanceStatusData: InstanceStatusFields;
  readonly environmentAndCeleryQueueStatus: EnvironmentAndCeleryQueueStatus;
  readonly environmentsWithFailedZeroTrust: EnvironmentsWithFailedZeroTrustResponse;
  readonly isLoadingEnvironmentsWithFailedZeroTrust: boolean;
  readonly sessionsHistoryMonitorGraphFields: SessionsHistoryMonitorGraphFields;
  readonly clientsHistoryMonitorGraphFields: ClientsHistoryMonitorGraph;
  readonly usersHistoryMonitorGraphFields: UsersHistoryMonitorGraph;
  readonly environmentsCostGraphFields: EnvironmentsCostGraphFields;
  readonly monthlyEnvironmentsCostGraphFields: MonthlyEnvironmentsCostGraphFields;
  readonly periodFilterSessions: PeriodCountAccess;
  readonly periodFilterHistory: PeriodCountAccess;
  readonly periodFilterInstances: PeriodCountAccess;
  readonly periodFilterEnvironmentsCost: PeriodCountAccess;
  readonly isLoadingSessionCard: boolean;
  readonly isLoadingClientCard: boolean;
  readonly flavorsCostFields: ListFlavorsCostFields;
  readonly flavorsCostPeriodFilter: PeriodCountAccess;
  readonly flavorsCostIsLoading: boolean;
  readonly flavorEnvironmentsIsLoading: boolean;
  readonly flavorEnvironmentsData: FlavorEnvironmentsData;
  readonly instancesWithSessionTimeout: InstancesWithSessionTimeoutFields;
}
